import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import DestinationsCategories from '../components/DestinationsCategories';
import DestinationTemplate from '../components/DestinationTemplate';

const MainBody = styled.div`
  width: 100%;
`;

const Title = styled.div`
  font-size: 30px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  color: #385963;
  font-weight: 600;
  margin: 100px 100px 10px;
  gap: 30px;
  height: 500px;

  @media (max-width: 768px) {
    font-size: 28px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    text-align: center;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NavContainer = styled.nav`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 30px 20px;
  background-color: transparent;
  position: relative;
  width: 100%;
  align-items: center;

  @media (max-width: 768px) {
    padding: 20px 20px 0px;
  }
`;

const NavTabs = styled.ul`
  display: flex;
  list-style: none;
  align-items: center;
  gap: 20px;
  padding: 0;

  li {
    margin-right: 10px;

    a {
      text-decoration: none;
      color: #385963;
      font-size: 16px;
      font-weight: 500;
      padding: 10px 0px;
      margin: 0 10px;
      position: relative;
      transition: color 0.4s;

      &:hover {
        color: #91abb3;
      }

      &::before {
        content: "";
        position: absolute;
        width: 0;
        height: 2px;
        bottom: 0;
        left: 50%;
        background-color:#91abb3;
        transition: all 0.4s;
      }

      &:hover::before {
        width: 100%;
        left: 0;
      }

      &.active {
        color: #91abb3;
        font-weight: 700;

        &::before {
          width: 100%;
          left: 0;
        }
      }
    }
  }
  
  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: center;

   li a {
      font-size: 14px;
      white-space: normal;
      text-align: center;
    }
  }

  @media (max-width: 992px) {
   li a {
      font-size: 14px;
      white-space: normal;
      text-align: center;
    }
  }

  @media (max-width: 768px) {
  gap:10px;

  li a {
      font-size: 14px;
      white-space: normal;
      text-align: center;
    }
  }

  @media (max-width: 576px) {
  gap:8px;

  li  a {
      font-size: 10px;
      white-space: normal;
      text-align: center;
    }
  }

  
`;


const CategoryInfo = styled.div`
  text-align: center;
  margin-top: 20px;
  max-width: 800px;
  color: #385963;

  h2 {
    margin-bottom: 10px;
  }

  p {
    margin: 0;
  }

  @media (max-width: 768px) {
    max-width: 90%;
    margin-bottom: 40px;
    h2 {
      font-size: 22px;
    }
    p {
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    max-width: 80%;
     margin-bottom: 20px;
    h2 {
      font-size: 16px;
    }
    p {
      font-size: 10px;
    }
  }
`;


const Destinations = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const location = useLocation();
  const { state } = location;
  const [activeComponent, setActiveComponent] = useState(state?.activeComponent || '');
  const [categoryId, setCategoryId] = useState(state?.categoryId || '');
  const selectedLanguage = sessionStorage.getItem('selectedLanguage') || 'en';

  useEffect(() => {
    if (state?.activeComponent) setActiveComponent(state.activeComponent);
    if (state?.categoryId) setCategoryId(state.categoryId);
  }, [state]);

  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const handleDestinationNavigation = (component, newCategoryId) => {
    navigate('/destinations', { state: { activeComponent: component, categoryId: newCategoryId } });
  };

  const [destinationCategories, setDestinationCategories] = useState([]);

  useEffect(() => {
    const fetchDestinationCategories = async () => {
      try {
        const response = await fetch(`${BASE_URL}DestinationCategory`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setDestinationCategories(data);
      } catch (error) {
        console.error('Error fetching destination categories:', error);
      }
    };
    fetchDestinationCategories();
  }, [BASE_URL]);

  return (
    <>
      <Header>
        <NavContainer>
          <NavTabs>
            {destinationCategories.map((category) => (
              <li
                key={category.id}
                onClick={() => handleDestinationNavigation('mainDestinations', category.id)}
              >
                <Link
                  to="#"
                  className={category.id === categoryId ? 'active' : ''}
                >
                  {category.name[selectedLanguage]}
                </Link>
              </li>
            ))}
          </NavTabs>

          

          {activeComponent !== 'DestinationTemplate' && (
            <hr
              style={{
                margin: '20px 0',
                borderTop: '2px solid #538392',
                width: '90%',
                placeSelf: 'center',
              }}
            />
          )}

          {categoryId && activeComponent === 'mainDestinations' && (
            <CategoryInfo>
              {(() => {
                const selectedCategory = destinationCategories.find(
                  (cat) => cat.id === categoryId
                );
                if (!selectedCategory) return null;
                return (
                  <>
                    <h2>{selectedCategory.name[selectedLanguage]}</h2>
                    <p>{selectedCategory.description?.[selectedLanguage]}</p>
                  </>
                );
              })()}
            </CategoryInfo>
          )}


        </NavContainer>
      </Header>

      <MainBody>
        {activeComponent === '' && (
          <Title>
            <div>{i18n.t('discoverDestinations')}</div>
            <div>{i18n.t('startChooseCategory')}</div>
          </Title>
        )}
        {activeComponent === 'mainDestinations' && (
          <DestinationsCategories categoryId={categoryId} />
        )}
        {activeComponent === 'DestinationTemplate' && <DestinationTemplate />}
      </MainBody>
    </>
  );
};

export default Destinations;
